import { callApi } from '@/api/callApi';
import {
  API_ENDPOINT,
  API_URL_ORDER_SERVICE,
  API_URL_USER_SERVICE,
} from '@/api/constant';
import Breadcrumb from '@/components/common/Breadcrumb';
import CommonTable, {
  Action,
  TableColumn,
} from '@/components/common/CommonTable';
import Layout from '@/components/Layout/Layout';
import Card from '@/components/UI/Card';
import DetailsCard from '@/components/UI/DetailsCard';
import { Customer, ICustomerDetails } from '@/interfaces/customer.interface';
import { IOrderResponse, Order } from '@/interfaces/order.interface';
import { formatDateTime, toFixedThree } from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const CustomerDetails: React.FC = () => {
  const navigate = useNavigate();
  const breadcrumb = [
    { label: 'Customer', href: RoutesLink.customer },
    { label: 'Details' },
  ];
  const { id } = useParams();
  const [state, setState] = useState<ICustomerDetails>({
    customer: null,
    order: [],
    isLoading: false,
    isLoadingOrder: false,
  });

  useEffect(() => {
    fetchCustomers();
    fetchOrders();
  }, []);

  const fetchCustomers = async () => {
    try {
      setState((prev) => ({
        ...prev,
        isLoading: true,
      }));
      const PATH = `${API_URL_USER_SERVICE}${API_ENDPOINT.users}/detail-admin/${id}`;
      const res = await callApi<Customer>(PATH, { method: 'GET' });
      setState((prev) => ({
        ...prev,
        customer: res,
        isLoading: false,
      }));
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  const fetchOrders = async () => {
    try {
      setState((prev) => ({
        ...prev,
        isLoadingOrder: true,
      }));
      let PATH = `${API_URL_ORDER_SERVICE}${API_ENDPOINT.order}?userId=${id}`;
      const res = await callApi<IOrderResponse>(PATH, { method: 'GET' });
      setState((prev) => ({
        ...prev,
        order: res.data,
        isLoadingOrder: false,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoadingOrder: true,
      }));
    }
  };

  const orderColumns: TableColumn<Order>[] = [
    {
      key: 'orderId',
      header: 'Order ID',
      render: (order) => order.orderId,
      className: 'p-3',
    },
    {
      key: 'dateTime',
      header: 'Date Time',
      render: (order) => formatDateTime(order.deliveryTime),
    },
    {
      key: 'customer',
      header: 'Customer',
      render: (order) =>
        `${order.personalInfo.firstName} ${order.personalInfo.lastName}`,
    },
    {
      key: 'total',
      header: 'Total Amount BD',
      render: (order) => `${order.grandTotal} BD`,
    },
    {
      key: 'orderStatus',
      header: 'Order Status',
      render: (order) => order.status,
    },
    {
      key: 'paymentType',
      header: 'Payment Type',
      render: (order) => order.paymentMethod?.type ?? 'N/A',
    },
    {
      key: 'items',
      header: 'Items',
      render: (order) => order.orderItems?.length ?? 0,
    },
  ];

  const actions: Action<Order>[] = [
    {
      text: 'View',
      onClick: (order) => {
        navigate(`${RoutesLink.orderDetail}/${order.id}`);
      },
    },
  ];

  const userDetailsData = [
    { label: 'Email', value: state.customer?.email },
    { label: 'Phone Number', value: state.customer?.phone },
  ];
  // const addressLine = [
  //   state.customer?.addresses[0]?.buildingName,
  //   state.customer?.addresses[0]?.aptNumber,
  //   state.customer?.addresses[0]?.floor,
  //   state.customer?.addresses[0]?.street,
  //   state.customer?.addresses[0]?.block,
  //   state.customer?.addresses[0]?.companyName,
  //   state.customer?.addresses[0]?.additionalDirection,
  //   state.customer?.addresses[0]?.phone,
  // ]
  //   .filter((item) => item && item.trim() !== '')
  //   .join(', ');

  const userAddress = [
    {
      label: 'Address Type',
      value: state?.customer?.addresses[0]?.addressType || '',
    },
    {
      label: 'Building Name',
      value: `${state?.customer?.addresses[0]?.buildingName || ''}`,
    },
    {
      label: 'Apartment Number',
      value: `${state?.customer?.addresses[0]?.aptNumber || ''}`,
    },
    {
      label: 'Floor',
      value: `${state?.customer?.addresses[0]?.floor || ''}`,
    },
    {
      label: 'Street',
      value: `${state?.customer?.addresses[0]?.street || ''}`,
    },
    {
      label: 'Block',
      value: `${state?.customer?.addresses[0]?.block || ''}`,
    },
    {
      label: 'Delivery Address',
      value: `${state?.customer?.addresses[0]?.addressLabel || ''}`,
    },
    {
      label: 'Company Name',
      value: `${state?.customer?.addresses[0]?.companyName || ''}`,
    },
  ];

  // const userAddress = [{ label: 'Address', value: addressLine }];
  const orderDetails = [
    { label: 'Total Orders', value: state.customer?.orderDetails?.orders },
    { label: 'Amount Spent', value: `${toFixedThree(state?.customer?.orderDetails?.spends || 0)} BHD` },
    { label: 'Total Loyalty Pts', value: state.customer?.loyalty?.points },
  ];
  return (
    <Layout>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <Breadcrumb items={breadcrumb} />
        </div>
        <div className="">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
            <div className="space-y-5">
              <div>
                <DetailsCard
                  userName={state.customer?.fullName}
                  details={userDetailsData}
                  customerSince={`Customer Since ${formatDateTime(state.customer?.createdAt, 'YYMMMDDHH')}`}
                />
              </div>
              <div>
                <DetailsCard title={"Address Detail's"} details={userAddress} />
              </div>
            </div>

            <div className="space-y-6">
              {orderDetails?.map((order) => (
                <div className="bg-white p-6 rounded-2xl shadow-sm">
                  <h3 className="text-base font-medium text-gray-600">
                    {order.label}
                  </h3>
                  <p className="text-sm text-gray-300 mt-1">
                    {order.value || '0'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div>
          <Card>
            <h3 className="text-base font-medium text-gray-600">Orders</h3>
            <CommonTable
              columns={orderColumns}
              data={state.order}
              emptyMessage="No Order found"
              loading={state.isLoadingOrder}
              actions={actions}
            />
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default CustomerDetails;
